<template>
  <div class="payment-container">
    <div class="payment-card">
      <div class="payment-header">
        <h2>PayFast Payment Gateway</h2>
        <div class="payment-logo">
          <!-- <img src="/assets/payfast-logo.png" alt="PayFast" onerror="this.src='https://via.placeholder.com/120x40?text=PayFast'"> -->
        </div>
      </div>

      <div class="payment-body">
        <div v-if="!alreadyPaid && !transactionSuccess && loading" class="payment-loader">
          <div class="spinner"></div>
          <p>Processing your payment request...</p>
        </div>

        <div v-else-if="message" class="payment-message success">
          <i class="fas fa-info-circle"></i>
          <p>{{ message }}</p>
        </div>

        <div v-if="error" class="payment-message error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>

        <!-- Already Paid Invoice Details -->
        <div v-if="alreadyPaid && currentInvoice" class="payment-details already-paid">
          <div class="payment-status-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h3>Invoice Already Paid</h3>
          <p class="payment-status-message">This invoice has already been processed and paid.</p>
          
          <div class="payment-info">
            <div class="payment-info-item">
              <span>Invoice ID:</span>
              <strong>{{ currentInvoice.id }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Customer:</span>
              <strong>{{ currentInvoice.customerName }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Amount:</span>
              <strong>{{ getTotalAmount(currentInvoice) }} {{ currentInvoice.currency }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Date:</span>
              <strong>{{ formatDate(currentInvoice.invoiceDate) }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Status:</span>
              <strong class="status-paid">{{ currentInvoice.status.toUpperCase() }}</strong>
            </div>
          </div>
          
          <div class="payment-actions">
            <button @click="goToDashboard" class="btn-primary">
              <i class="fas fa-home"></i> Go to Dashboard
            </button>
          </div>
        </div>

        <!-- Transaction Success Details -->
        <div v-if="transactionSuccess && !alreadyPaid" class="payment-details">
          <div class="payment-status-icon success">
            <i class="fas fa-check-circle"></i>
          </div>
          <h3>Payment Successful</h3>
          <div class="payment-info">
            <div class="payment-info-item">
              <span>Transaction ID:</span>
              <strong>{{ $route.query.transaction_id }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Amount:</span>
              <strong>{{ $route.query.transaction_amount }} {{ $route.query.transaction_currency }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Reference:</span>
              <strong>{{ $route.query.basket_id }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Payment Method:</span>
              <strong>{{ $route.query.PaymentName }}</strong>
            </div>
          </div>
          
          <div class="payment-actions">
            <button @click="goToDashboard" class="btn-primary">
              <i class="fas fa-home"></i> Go to Dashboard
            </button>
            <button @click="reloadInvoice" class="btn-secondary">
              <i class="fas fa-sync"></i> Reload Invoice
            </button>
          </div>
        </div>

        <!-- Transaction Failed Details -->
        <div v-if="transactionFailed && !alreadyPaid" class="payment-details failed-payment">
          <div class="payment-status-icon error">
            <i class="fas fa-times-circle"></i>
          </div>
          <h3>Payment Failed</h3>
          <div class="payment-info">
            <div class="payment-info-item">
              <span>Error Code:</span>
              <strong>{{ $route.query.err_code }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Error Message:</span>
              <strong>{{ decodeURIComponent($route.query.err_msg || '') }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Reference:</span>
              <strong>{{ $route.query.basket_id }}</strong>
            </div>
          </div>
          
          <div class="payment-actions">
            <button @click="retryPayment" class="btn-primary">
              <i class="fas fa-redo"></i> Retry Payment
            </button>
            <button @click="goToDashboard" class="btn-secondary">
              <i class="fas fa-home"></i> Go to Dashboard
            </button>
          </div>
        </div>

        <!-- Initial Payment Details -->
        <div v-if="paymentRequest && !error && !transactionSuccess && !transactionFailed && !alreadyPaid" class="payment-details">
          <h3>Payment Details</h3>
          <div class="payment-info">
            <div class="payment-info-item">
              <span>Amount:</span>
              <strong>{{ paymentRequest.txnAmt }} {{ paymentRequest.currencyCode }}</strong>
            </div>
            <div class="payment-info-item">
              <span>Reference:</span>
              <strong>{{ paymentRequest.basketId }}</strong>
            </div>
          </div>

          <div class="payment-countdown" v-if="countdown > 0">
            <div class="countdown-circle">
              <span>{{ countdown }}</span>
            </div>
            <p>Redirecting to secure payment page...</p>
          </div>
        </div>
      </div>

      <div class="payment-footer">
        <p>Secure payment powered by PayFast</p>
        <div class="security-badges">
          <i class="fas fa-lock"></i>
          <i class="fas fa-shield-alt"></i>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

export default {
  name: "PayFastPaymentGateway",
  data() {
    return {
      message: null,
      error: null,
      formURL: null,
      paymentRequest: null,
      loading: false,
      countdown: 5,
      alreadyPaid: false,
      currentInvoice: null,
      countdownInterval: null
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("pricing", ["invoicesByUserId"]),
    transactionSuccess() {
      return this.$route.query.transaction_id &&
        (this.$route.query.err_code === "000" || this.$route.query.err_code === "00" ) &&
        this.$route.query.basket_id;
    },
    transactionFailed() {
      return this.$route.query.status === "cancel" ||
        (this.$route.query.err_code && this.$route.query.err_code !== "000" && this.$route.query.err_code !== "00");
    },
    currentInvoiceId() {
      return this.$route.params.id;
    }
  },
  methods: {
    ...mapActions("pricing", ["fetchInvoiceByUserId", "processPayment"]),

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getTotalAmount(invoice) {
      if (!invoice || !invoice.details || !invoice.details.length) {
        return 0;
      }
      return invoice.details.reduce((total, item) => total + item.amount, 0);
    },

    goToDashboard() {
      this.$router.push("/user/dashboard");
    },

    retryPayment() {
      this.$router.replace({
        path: `/payment/PayFast/${this.currentInvoiceId}`
      }).then(() => {
        this.resetState();
        this.loadInvoice(this.currentInvoiceId).then(() => {
          if (!this.alreadyPaid) {
            this.initiatePayment(this.currentInvoiceId);
          }
        });
      }).catch(() => {});
    },

    reloadInvoice() {
      if (this.currentInvoiceId) {
        this.loadInvoice(this.currentInvoiceId);
      }
    },

    resetState() {
      this.message = null;
      this.error = null;
      this.formURL = null;
      this.paymentRequest = null;
      this.alreadyPaid = false;
      this.currentInvoice = null;
      
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      
      this.countdown = 5;
    },

    async loadInvoice(invoiceID) {
      if (!invoiceID) return;
      
      this.loading = true;
      this.message = "Fetching invoice details, please wait...";
      this.error = null;
      
      try {
        await this.fetchInvoiceByUserId(this.user.id);
        
        const invoice = this.invoicesByUserId[this.user.id]?.find(
          inv => inv.id == invoiceID
        );
        
        if (invoice) {
          this.currentInvoice = invoice;
          
          if (invoice.status && invoice.status.toLowerCase() === 'paid') {
            this.alreadyPaid = true;
            this.message = null;
          } else {
            this.alreadyPaid = false;
            this.message = "Invoice information updated.";
            setTimeout(() => {
              this.message = null;
            }, 3000);
          }
        } else {
          this.error = "Invoice not found.";
        }
      } catch (error) {
        this.error = "Failed to fetch invoice details.";
      } finally {
        this.loading = false;
      }
    },

    async initiatePayment(invoiceID) {
      try {
        this.loading = true;
        this.message = "Preparing payment gateway, please wait...";
        this.error = null;

        const response = await this.processPayment({
          invoiceId: invoiceID,
          serviceCode: "PayFast",
          data: {
            redirectUrl: `${window.location.origin}/payment/PayFast/${invoiceID}`,
            failureUrl: `${window.location.origin}/payment/PayFast/${invoiceID}?status=cancel`,
          }
        });

        if (response && response.data) {
          this.formURL = response.data.paymentFormURL;
          this.paymentRequest = response.data.paymentRequest;
          this.startCountdown({ onCountdownComplete: this.submitPaymentForm });
        } else {
          this.error = "Invalid response from payment gateway.";
        }
      } catch (error) {
        this.error = "Some error occurred while fetching data from payment gateway.";
      } finally {
        this.loading = false;
      }
    },

    submitPaymentForm() {
      try {
        if (!this.formURL || !this.paymentRequest) {
          this.error = "Payment information is missing";
          return;
        }

        this.loading = true;
        this.message = "Connecting to payment gateway...";

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.formURL;
        form.style.display = 'none';

        const addInput = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };

        const paymentRequest = {
          CURRENCY_CODE: this.paymentRequest.currencyCode,
          MERCHANT_ID: this.paymentRequest.merchantId,
          MERCHANT_NAME: this.paymentRequest.merchantName,
          TOKEN: this.paymentRequest.token,
          BASKET_ID: this.paymentRequest.basketId,
          TXNAMT: this.paymentRequest.txnAmt,
          ORDER_DATE: this.paymentRequest.orderDate,
          SUCCESS_URL: this.paymentRequest.successUrl,
          FAILURE_URL: this.paymentRequest.failureUrl,
          CHECKOUT_URL: this.paymentRequest.checkoutUrl,
          CUSTOMER_EMAIL_ADDRESS: this.paymentRequest.customerEmailAddress,
          CUSTOMER_MOBILE_NO: this.paymentRequest.customerMobileNo,
          MERCHANT_CUSTOMER_ID: this.paymentRequest.merchantCustomerId,
          VERSION: this.paymentRequest.version,
          TXNDESC: this.paymentRequest.txnDesc,
          PROCCODE: this.paymentRequest.procCode,
          TRAN_TYPE: this.paymentRequest.tranType,
        };

        for (const [key, value] of Object.entries(paymentRequest)) {
          if (value !== null && value !== undefined) {
            addInput(key, value);
          }
        }

        // Add the user agent (if needed by PayFast)
        addInput('MERCHANT_USERAGENT', 'Mozilla/5.0 (Windows NT 6.1; Win64; x64; rv:47.0) Gecko/20100101 Firefox/47.0');

        // Add invoice details as items 
        // items.Add(new
        // {
        //     SKU = detail.ProductSourceID,
        //     NAME = detail.ProductName,
        //     PRICE = Math.Max((detail.Amount - detail.Discount), 0).ToString(),
        //     QTY = "1"
        // });

        if (Array.isArray(this.paymentRequest.items)) {
          this.paymentRequest.items.forEach((item, index) => {
            for (const [key, value] of Object.entries(item)) {
              if (value !== null && value !== undefined) {
                addInput(`ITEMS[${index}][${key}]`, value);
              }
            }
          });
        }

        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        this.error = "Failed to connect to payment gateway. Please try again.";
        this.loading = false;
      }
    },

    startCountdown({ onCountdownComplete }) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      
      this.countdown = 5;
      this.message = `You will be redirected to PayFast Payment Gateway in ${this.countdown} seconds.`;
      
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
          this.message = `You will be redirected to PayFast Payment Gateway in ${this.countdown} seconds.`;
        }

        if (this.countdown === 0) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
          onCountdownComplete();
        }
      }, 1000);
    },

    showSuccessMessage() {
      const transactionId = this.$route.query.transaction_id || '';
      const amount = this.$route.query.transaction_amount || '';
      const currency = this.$route.query.transaction_currency || '';

      return Swal.fire({
        title: "Transaction Successful",
        html: `
          <div class="swal-content">
            <div class="swal-message">Congratulations! Your payment has been processed successfully.</div>
            <div class="swal-details">
              <div class="swal-detail-item">
                <span class="swal-label">Transaction ID:</span>
                <span class="swal-value">${transactionId}</span>
              </div>
              <div class="swal-detail-item">
                <span class="swal-label">Amount:</span>
                <span class="swal-value">${amount} ${currency}</span>
              </div>
            </div>
          </div>
        `,
        icon: "success",
        width: '40em',
        showCancelButton: true,
        confirmButtonText: "Go to Dashboard",
        cancelButtonText: "Stay",
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: 'custom-swal-cancel'
        },
        didOpen: () => {
          const style = document.createElement('style');
          style.innerHTML = `
            .swal-content {
              text-align: center;
              padding: 0.5rem 0;
            }
            .swal-message {
              margin-bottom: 1.25rem;
              font-size: 1rem;
            }
            .swal-details {
              display: inline-block;
              text-align: left;
              background-color: #f8f9fa;
              border-radius: 8px;
              padding: 1rem;
              margin: 0 auto;
            }
            .swal-detail-item {
              margin-bottom: 0.5rem;
              display: flex;
              justify-content: space-between;
              gap: 1rem;
            }
            .swal-detail-item:last-child {
              margin-bottom: 0;
            }
            .swal-label {
              font-weight: 600;
              color: #495057;
            }
            .swal-value {
              color: #212529;
            }
          `;
          document.head.appendChild(style);
        }
      });
    },

    showFailureMessage() {
      const errorCode = this.$route.query.err_code || '';
      const errorMsg = decodeURIComponent(this.$route.query.err_msg || 'Transaction failed');

      return Swal.fire({
        title: "Transaction Failed",
        html: `
          <div class="swal-content">
            <div class="swal-message">${errorMsg}</div>
            ${errorCode ? `
            <div class="swal-details">
              <div class="swal-detail-item">
                <span class="swal-label">Error Code:</span>
                <span class="swal-value">${errorCode}</span>
              </div>
            </div>
            ` : ''}
          </div>
        `,
        icon: "error",
        width: '35em',
        showDenyButton: true,
        confirmButtonText: "Go to Dashboard",
        denyButtonText: "Retry Payment",
        cancelButtonText: "Stay",
        showCancelButton: true,
        confirmButtonColor: "#6c757d",
        denyButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          denyButton: 'custom-swal-deny',
          cancelButton: 'custom-swal-cancel'
        },
        didOpen: () => {
          const style = document.createElement('style');
          style.innerHTML = `
            .swal-content {
              text-align: center;
              padding: 0.5rem 0;
            }
            .swal-message {
              margin-bottom: 1.25rem;
              font-size: 1rem;
            }
            .swal-details {
              display: inline-block;
              text-align: left;
              background-color: #f8f9fa;
              border-radius: 8px;
              padding: 1rem;
              margin: 0 auto;
            }
            .swal-detail-item {
              margin-bottom: 0.5rem;
              display: flex;
              justify-content: space-between;
              gap: 1rem;
            }
            .swal-detail-item:last-child {
              margin-bottom: 0;
            }
            .swal-label {
              font-weight: 600;
              color: #495057;
            }
            .swal-value {
              color: #212529;
            }
            .custom-swal-deny {
              font-weight: 600 !important;
            }
          `;
          document.head.appendChild(style);
        }
      });
    }
  },
  async created() {
    if (this.transactionSuccess) {
      const result = await this.showSuccessMessage();
      if (result.isConfirmed) {
        this.$router.push("/user/dashboard");
      }
      //  else if (result.dismiss === Swal.DismissReason.cancel) {
      //   this.reloadInvoice();
      // }
    }
    else if (this.transactionFailed) {
      const result = await this.showFailureMessage();
      if (result.isConfirmed) {
        this.$router.push("/user/dashboard");
      } else if (result.isDenied) {
        this.retryPayment();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // this.reloadInvoice();
      }
    }
    else {
      const invoiceID = this.$route.params.id;
      if (invoiceID) {
        await this.loadInvoice(invoiceID);
        if (!this.alreadyPaid) {
          await this.initiatePayment(invoiceID);
        }
      } else {
        this.error = "Invalid parameters";
      }
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
};
</script>
  
<style scoped>
.payment-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
  padding: 2rem;
  background-color: #f8f9fa;
}

.payment-card {
  width: 100%;
  max-width: 700px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  margin: 70px 0px;
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: white;
}

.payment-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.payment-logo {
  height: 40px;
}

.payment-logo img {
  height: 100%;
  object-fit: contain;
}

.payment-body {
  padding: 2rem;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.payment-message {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.payment-message i {
  margin-right: 0.75rem;
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.payment-message p {
  margin: 0;
  line-height: 1.5;
}

.payment-message.success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border-left: 4px solid #28a745;
}

.payment-message.error {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border-left: 4px solid #dc3545;
}

.payment-details {
  margin-top: 1rem;
  text-align: center;
}

.payment-details h3 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
}

.payment-status-icon {
  margin-bottom: 1rem;
}

.payment-status-icon i {
  font-size: 3rem;
  color: var(--primary-color);
}

.payment-status-icon.success i {
  color: #28a745;
}

.payment-status-icon.error i {
  color: #dc3545;
}

.payment-status-message {
  margin-bottom: 1.5rem;
  color: #555;
}

.status-paid {
  color: var(--primary-color);
  font-weight: 700;
}

.payment-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-primary, .btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--secondary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-primary i, .btn-secondary i {
  margin-right: 0.5rem;
}

.payment-info {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin: 0 auto 1.5rem;
  max-width: 90%;
  text-align: left;
}

.payment-info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.payment-info-item:last-child {
  margin-bottom: 0;
}

.payment-countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;
}

.countdown-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.payment-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 123, 255, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.payment-footer {
  padding: 1rem 2rem;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.payment-footer p {
  margin: 0;
  color: #6c757d;
  font-size: 0.875rem;
}

.security-badges {
  display: flex;
  gap: 0.75rem;
  color: #6c757d;
}

.security-badges i {
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .payment-container {
    padding: 1rem;
  }

  .payment-header {
    padding: 1rem 1.5rem;
    flex-direction: column;
    align-items: flex-start;
  }

  .payment-logo {
    margin-top: 0.5rem;
  }

  .payment-body {
    padding: 1.5rem;
  }
  
  .payment-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>